import { useFeatureFlag } from 'src/integrations/feature-flags/feature-flags';
import { FEATURE_FLAGS } from '@erp/shared-types';
import EmailViewer from './EmailViewer';
import { EmailThreadViewer } from './EmailThreadViewer';
import { EmailViewerProps } from './email-viewer-types';

export interface SmartEmailViewerProps extends EmailViewerProps {
  /**
   * Conversation ID for thread display
   * If provided and threads are enabled, shows EmailThreadViewer
   */
  conversationId?: string | null;
  /** Document IDs to highlight (for showing source documents of invoices/POs) */
  highlightedDocumentIds?: string[];
  /** Email IDs to highlight */
  highlightedEmailIds?: string[];
}

/**
 * Smart wrapper that conditionally renders EmailViewer or EmailThreadViewer
 * based on feature flag and presence of conversationId
 */
export function SmartEmailViewer({
  conversationId,
  emailData,
  fetchOCR,
  fetchExtractedPurchaseOrder,
  defaultTab = 'email',
  highlightedDocumentIds,
  highlightedEmailIds,
}: SmartEmailViewerProps) {
  const { enabled: emailThreadsEnabled } = useFeatureFlag(
    FEATURE_FLAGS['email-threads-enabled'],
    false,
  );

  // Show thread viewer if:
  // 1. Feature flag is enabled
  // 2. conversationId is provided
  // 3. conversationId is not empty/null
  const shouldShowThreadViewer =
    emailThreadsEnabled && conversationId && conversationId.trim().length > 0;

  if (shouldShowThreadViewer) {
    return (
      <EmailThreadViewer
        conversationId={conversationId}
        fetchOCR={fetchOCR}
        fetchExtractedPurchaseOrder={fetchExtractedPurchaseOrder}
        highlightedDocumentIds={highlightedDocumentIds}
        highlightedEmailIds={highlightedEmailIds}
      />
    );
  }

  // Fallback to regular EmailViewer
  return (
    <EmailViewer
      emailData={emailData}
      fetchOCR={fetchOCR}
      fetchExtractedPurchaseOrder={fetchExtractedPurchaseOrder}
      defaultTab={defaultTab}
    />
  );
}
